import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../../context/SnackbarContextProvider';
import {
  useClientsContext,
  getLocationFilterParams,
} from '../../../../context/clients/ClientsContextProvider';
import { useCustomTiming } from '../../../../context/CustomTimingContextProvider';
import { uploadFiles } from '../../../../utils/uploadFiles';
import { getStartAndEndOfDay } from '../../../../utils';
import FormDialogHeader from './components/FormDialogHeader';
import FormSecondaryHeader from './components/FormSecondaryHeader';
import FormTabs from './components/FormTabs';
import QuestionsTab from './components/QuestionsTab';
import SettingsTab from './components/SettingsTab';
import ResponsesTab from './components/ResponsesTab';
import {
  FormDataType,
  FormTabType,
  Question,
  DEFAULT_FORM_SETTINGS,
  createDefaultQuestion,
  formDataSchema,
} from './types';
import { useFormsMutations } from '../../../../hooks/useFormsHook';
import { FormStatuses } from '../../../../services/operations/forms';
import { useFormsContext } from '../../../../context/forms/FormsContextProvider';

type Props = {
  open: boolean;
  onClose: () => void;
  formId?: string;
  copyMode?: boolean;
};

function AddEditForm({ open, onClose, formId, copyMode = false }: Props) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { selectedLocation, selectedClient } = useClientsContext();
  const { customTimingData } = useCustomTiming();
  const [activeTab, setActiveTab] = useState<FormTabType>('questions');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  // const [copyClientId, setCopyClientId] = useState<string>('');
  // const [copyBranchIds, setCopyBranchIds] = useState<string[]>([]);
  const { createFormMutation, updateFormMutation } = useFormsMutations();
  const { formData } = useFormsContext();

  const isEditMode = !!formId || copyMode;

  const buildQuestionsValues = (questions: Question[]) => {
    return questions.map((question) => {
      return createDefaultQuestion(question?.type, question?._id!, question);
    });
  };

  const getDefaultValues = (): FormDataType => {
    const defaults = {
      title: isEditMode ? formData?.title || '' : '',
      description: '',
      questions: [createDefaultQuestion('short-answer', `q-${Date.now()}`)],
      settings: DEFAULT_FORM_SETTINGS,
      status: FormStatuses.DRAFT,
    } as FormDataType;

    if (isEditMode && formData) {
      return {
        ...defaults,
        title: copyMode ? `${formData.title} - עותק` : formData.title,
        description: formData.description,
        questions: buildQuestionsValues(formData.questions as Question[]),
        settings: {
          allQuestionsRequired: formData.allQuestionsRequired || false,
          countRespondents: formData.countRespondents || false,
          anonymousForm: formData.isAnonymous || false,
          formForCustomer: formData.formForCustomers || false,
          branchIds:
            formData.branches?.map((b: { _id: string }) => b._id) || [],
          subBranchIds:
            formData.subBranches?.map((b: { _id: string }) => b._id) || [],
          departmentIds:
            typeof formData.recipients?.departments === 'string'
              ? 'all'
              : formData.recipients?.departments?.map(
                  (d: { _id: string }) => d._id,
                ) || [],
          recipientIds:
            formData.recipients?.users?.map((u: { _id: string }) => u._id) ||
            [],
          timingType: formData.isSingleResponse ? 'single' : 'scheduled',
          schedulingType: formData.scheduling?.schedulingType || 'default',
          startDate: formData.scheduling?.startDate || '',
          endDate: formData.scheduling?.endDate || '',
          startTime: formData.scheduling?.startTime || '',
          endTime: formData.scheduling?.endTime || '',
          time: formData.scheduling?.time || '',
          dayOfWeek: formData.scheduling?.dayOfWeek || 0,
          customTiming: formData.scheduling?.customTiming || null,
          confirmationText: formData.confirmationMessage,
        },
      };
    }

    const { branchId, subBranchId } = getLocationFilterParams(selectedLocation);
    return {
      ...defaults,
      settings: {
        ...DEFAULT_FORM_SETTINGS,
        branchIds: branchId ? [branchId] : [],
        subBranchIds: subBranchId ? [subBranchId] : [],
      },
    };
  };

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormDataType>({
    resolver: zodResolver(formDataSchema),
    defaultValues: getDefaultValues(),
  });
  const title = watch('title');

  useEffect(() => {
    if (!open) return;

    if (isEditMode && formData) {
      reset(getDefaultValues());
    } else if (!isEditMode) {
      reset(getDefaultValues());
    }
  }, [formData, isEditMode, open, formId, selectedLocation?.id]);

  const handleClose = () => {
    reset();
    setActiveTab('questions');
    // setCopyClientId('');
    // setCopyBranchIds([]);
    onClose();
  };

  const handleSaveDraft = () => {
    setValue('status', 'draft');
    handleSubmit(onSubmit)();
  };

  const handlePublish = () => {
    setValue('status', 'published');
    handleSubmit(onSubmit)();
  };

  const getAdditionalFieldsByType = (question: Question) => {
    switch (question.type) {
      case 'linear-scale':
        const linearScale = {
          min: question.fromValue,
          max: question.toValue,
          minLabel: question.fromLabel,
          maxLabel: question.toLabel,
        };
        return { linearScale };
      case 'rating':
        const rating = {
          max: question.maxRating,
          icon: question.icon,
        };
        return { rating };
      case 'date':
        const dateConfig = {
          range: question.dateType === 'range',
        };
        return { dateConfig };
      case 'time':
        const timeConfig = {
          range: question.timeType === 'range',
        };
        return { timeConfig };
      case 'file-upload':
        const fileUploadConfig = {
          maxFileSizeMB: question.fileUploadConfig?.maxFileSizeMB,
          allowedTypes: question.fileUploadConfig?.allowedTypes,
          maxFiles: question.fileUploadConfig?.maxFiles ?? 1,
        };
        return { fileUploadConfig };
      case 'range':
        const rangeConfig = {
          rangeType: question?.rangeConfig?.rangeType,
          form: question?.rangeConfig?.form,
          min: question?.rangeConfig?.min || 0,
          max: question?.rangeConfig?.max || 100,
          minLabel: question?.rangeConfig?.minLabel,
          maxLabel: question?.rangeConfig?.maxLabel,
          unit: question?.rangeConfig?.unit || '',
          canUploadImage: question?.rangeConfig?.canUploadImage,
        };
        return { rangeConfig };
      default:
        return null;
    }
  };

  // --------------- Helpers for uploading a single image field ---------------

  /** Upload a File to S3 and return the public URL, or pass through existing string URLs.
   *  Returns null (not undefined) when cleared so JSON.stringify keeps the key in the payload. */
  const uploadImageField = async (
    image: File | string | undefined,
  ): Promise<string | null> => {
    if (!image) return null;
    if (typeof image === 'string') return image; // already a URL
    const [uploaded] = await uploadFiles([image]);
    return uploaded?.url ?? null;
  };

  // --------------- Process questions: upload all images ---------------

  const processQuestions = async (questions: Question[]) => {
    return Promise.all(
      questions.map(async (question, index) => {
        // Upload question-level image
        const imageUrl = await uploadImageField(question.image);
        const { id, title, type, mandatory } = question;
        const additionalFields = getAdditionalFieldsByType(question);
        const shouldSendID = isEditMode && !copyMode;

        const base = {
          title,
          type,
          required: mandatory,
          image: imageUrl,
          displayOrder: index,
          ...(additionalFields && { ...additionalFields }),
          ...(shouldSendID && !id?.startsWith('q-') && { id }), // only include ID if in edit mode, not copy mode, and ID doesn't look like a new question
        };

        if (question.type === 'dropdown') {
          const processedOptions = question.options.map((opt, optIndex) => ({
            label: opt.title || `${t('forms.dialog.option')} ${optIndex + 1}`,
            key: opt.id,
            isOther: false,
          }));

          return {
            ...base,
            options: processedOptions,
          };
        }

        // Upload option images for multiple-choice / checkbox
        if (
          question.type === 'multiple-choice' ||
          question.type === 'checkbox'
        ) {
          const processedOptions = await Promise.all(
            question.options.map(async (opt, optIndex) => {
              return {
                label:
                  opt.title || `${t('forms.dialog.option')} ${optIndex + 1}`,
                key: opt.id,
                isOther: false,
                image: await uploadImageField(opt.image),
              };
            }),
          );

          const otherImageUrl = await uploadImageField(
            question.otherOptionImage,
          );

          const otherOption = {
            label: t('forms.dialog.other') || 'other',
            image: otherImageUrl,
            // id: `${question.id}-other`,
            key: `${question.id}-other`,
            isOther: true,
          };

          return {
            ...base,
            options: question?.hasOtherOption
              ? [...processedOptions, otherOption]
              : processedOptions,
            // otherOptionImage: otherImageUrl,
          };
        }

        return base;
      }),
    );
  };

  // --------------- Build scheduling payload (mirrors announcement) ---------------

  const buildCustomConfig = () => {
    if (!customTimingData) return {};

    const config: Record<string, any> = {
      repeatType: customTimingData.repeatType,
      endCondition: customTimingData.endCondition,
    };

    if (customTimingData.repeatEvery) {
      config.repeatEvery = customTimingData.repeatEvery;
    }

    if (
      customTimingData.repeatType === 'weekly' &&
      customTimingData.weeklyDays?.length > 0
    ) {
      config.weeklyDays = customTimingData.weeklyDays;
    }

    if (
      customTimingData.repeatType === 'monthly' &&
      customTimingData.monthlyDates?.length > 0
    ) {
      config.monthlyDates = customTimingData.monthlyDates;
    }

    if (
      customTimingData.endCondition === 'end_date' &&
      customTimingData.endDate
    ) {
      config.endDate = moment(customTimingData.endDate)
        .endOf('day')
        .toISOString();
    }

    if (
      customTimingData.endCondition === 'occurrences' &&
      customTimingData.occurrences
    ) {
      config.occurrences = customTimingData.occurrences;
    }

    return config;
  };

  const buildSchedulingPayload = (data: FormDataType) => {
    const { schedulingType, startDate, endDate, startTime, endTime } =
      data.settings;

    if (!schedulingType || schedulingType === '') return null;

    const { startOfDay } = getStartAndEndOfDay(
      startDate || moment().format('YYYY-MM-DD'),
    );
    const { endOfDay } = getStartAndEndOfDay(
      endDate || moment().format('YYYY-MM-DD'),
    );

    const basePayload: Record<string, any> = {
      schedulingType,
      ...(schedulingType === 'custom' ||
      schedulingType === 'daily_at_fixed_time'
        ? {}
        : { startDate: startOfDay }),
    };

    switch (schedulingType) {
      case 'default':
        return { ...basePayload, endDate: endOfDay };

      case 'all_day':
        return basePayload;

      case 'by_day_and_time':
        return { ...basePayload, startTime, endTime };

      case 'between_days_and_hours':
        return { ...basePayload, endDate: endOfDay, startTime, endTime };

      case 'daily_at_fixed_time':
        return { ...basePayload, startTime, endTime };

      case 'custom':
        return {
          ...basePayload,
          startTime,
          endTime,
          customConfig: buildCustomConfig(),
        };

      default:
        return basePayload;
    }
  };

  // --------------- Main submit ---------------

  const onSubmit = async (data: FormDataType) => {
    try {
      if (createFormMutation.isPending || updateFormMutation.isPending) return;
      setIsUploadingFiles(true);

      // 1) Determine clientId, branchIds & subBranchIds
      let clientId: string | undefined;
      let branchIds: string[];
      let subBranchIds: string[];

      if (selectedLocation?.isClient) {
        clientId = selectedLocation.id;
        branchIds = data.settings.branchIds;
        subBranchIds = data.settings.subBranchIds;
      } else if (selectedLocation?.isBranch) {
        clientId = selectedLocation.clientId;
        branchIds = data.settings.branchIds;
        subBranchIds = data.settings.subBranchIds;
      } else if (selectedLocation?.isSubBranch) {
        clientId = selectedLocation.clientId;
        branchIds = [selectedLocation.branchId];
        subBranchIds = [selectedLocation.id];
      } else {
        clientId = selectedClient?._id;
        branchIds = data.settings.branchIds;
        subBranchIds = data.settings.subBranchIds;
      }

      // 2) Upload all question / option images to S3
      const processedQuestions = await processQuestions(data.questions);

      // 3) Build scheduling payload (only when scheduled)
      const scheduling =
        data.settings.timingType === 'scheduled'
          ? buildSchedulingPayload(data)
          : null;

      // 4) Clean settings — remove form-only scheduling fields
      const {
        startDate,
        endDate,
        startTime,
        endTime,
        time,
        dayOfWeek,
        schedulingType,
        customTiming,
        branchIds: _branchIds,
        subBranchIds: _subBranchIds,
        ...cleanSettings
      } = data.settings;

      // 5) Build final payload
      const payload: any = {
        title: data.title,
        description: data.description,
        status: data.status,
        clientId: [clientId],
        branchId: branchIds,
        subBranchId: subBranchIds,
        questions: processedQuestions,
        allQuestionsRequired: cleanSettings.allQuestionsRequired,
        countRespondents: cleanSettings.countRespondents,
        isAnonymous: cleanSettings.anonymousForm,
        formForCustomers: cleanSettings.formForCustomer,
        confirmationMessage:
          cleanSettings.confirmationText || 'תשובתך התקבלה, תודה!',
        isSingleResponse: cleanSettings.timingType === 'single',
        recipients: {
          users: cleanSettings.recipientIds,
          ...(((Array.isArray(cleanSettings.departmentIds) &&
            cleanSettings.departmentIds.length > 0) ||
            cleanSettings.departmentIds === 'all') && {
            departments: cleanSettings.departmentIds,
          }),
        },
        ...(scheduling && { scheduling }),
      };

      if (isEditMode && formId && !copyMode) {
        await updateFormMutation.mutateAsync({ formId, payload });
      } else {
        await createFormMutation.mutateAsync(payload);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error in form submission:', error);
      showSnackbar({
        message: error?.message || 'שגיאה בהעלאת הקבצים',
        type: 'error',
      });
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'questions':
        return (
          <QuestionsTab
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
          />
        );
      case 'settings':
        return (
          <SettingsTab
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
            // copyMode={copyMode}
            // copyClientId={copyClientId}
            // setCopyClientId={setCopyClientId}
            // copyBranchIds={copyBranchIds}
            // setCopyBranchIds={setCopyBranchIds}
          />
        );
      case 'responses':
        return <ResponsesTab />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      sx={{ zIndex: 45 }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#D2D8DD',
          },
        },
      }}
    >
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Dialog Header */}
        <FormDialogHeader />

        {/* Secondary Header */}
        <FormSecondaryHeader
          title={title}
          onClose={handleClose}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          isSubmitting={isSubmitting || isUploadingFiles}
          copyMode={copyMode}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex-1 w-full max-w-[1200px] bg-white shadow-md">
            {/* Tabs */}
            <FormTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              responseCount={formData?.responsesCount || 0}
              copyMode={copyMode}
            />

            {/* Tab Content */}
            <div className="flex-1 h-[calc(100vh-170px)]">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default AddEditForm;
